/**
 * Client pour l'Edge Function `list-magasins` du back-office.
 * Retourne les magasins actifs géolocalisés, groupés par région. Le formulaire
 * alimente un select région → magasin (StepMagasin, mode localisation="magasin") :
 * l'utilisateur choisit d'abord la région, puis le magasin de cette région.
 *
 * L'URL est déduite de VITE_SUBMIT_LEAD_URL (dernier segment échangé) ou fournie
 * explicitement via VITE_LIST_MAGASINS_URL.
 */

export interface Magasin {
  id: string;
  departement: string;
  name: string;
  ville: string;
  label: string;
}

export interface RegionGroup {
  region: string;
  magasins: Magasin[];
}

/**
 * Rang numérique d'un département pour le tri.
 * 2A/2B (Corse) n'ont pas de valeur numérique : on les intercale entre 19 et 21,
 * là où l'ancien département 20 se trouvait (ordre INSEE).
 */
function departementRank(departement: string): number {
  const d = departement.trim().toUpperCase();
  if (d === "2A") return 20.1;
  if (d === "2B") return 20.2;
  const n = Number.parseInt(d, 10);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
}

/**
 * Trie une liste de magasins par numéro de département croissant puis, à
 * département égal, par libellé (A→Z, insensible casse/accents). Alimente le
 * select « Magasin » de StepMagasin (mode région → magasin).
 */
export function sortMagasins(magasins: Magasin[]): Magasin[] {
  return [...magasins].sort((a, b) => {
    const byDept = departementRank(a.departement) - departementRank(b.departement);
    if (byDept !== 0) return byDept;
    return a.label.localeCompare(b.label, "fr", { sensitivity: "base" });
  });
}

/**
 * Magasins d'une région donnée, triés (cf. sortMagasins). Retourne [] si la
 * région n'existe pas dans les groupes.
 */
export function magasinsForRegion(groups: RegionGroup[], region: string): Magasin[] {
  const group = groups.find((g) => g.region === region);
  return group ? sortMagasins(group.magasins) : [];
}

export function resolveEndpoint(): string | null {
  const explicit = import.meta.env.VITE_LIST_MAGASINS_URL;
  if (explicit) return explicit;
  const submit = import.meta.env.VITE_SUBMIT_LEAD_URL;
  if (!submit) return null;
  return submit.replace(/\/submit-lead(\/|$)/, "/list-magasins$1");
}

export type ListMagasinsResult =
  | { status: "ok"; groups: RegionGroup[] }
  | { status: "error" }
  | { status: "aborted" };

// Cache in-memory (vie du custom element) : la liste ne change pas pendant la session.
let cache: RegionGroup[] | null = null;

export async function listMagasins(signal?: AbortSignal): Promise<ListMagasinsResult> {
  if (cache) return { status: "ok", groups: cache };

  const endpoint = resolveEndpoint();
  if (!endpoint) {
    console.error("[listMagasins] missing VITE_SUBMIT_LEAD_URL / VITE_LIST_MAGASINS_URL");
    return { status: "error" };
  }

  try {
    const res = await fetch(endpoint, { method: "GET", signal });
    if (!res.ok) return { status: "error" };
    const data = (await res.json()) as RegionGroup[] | { error: string };
    if (Array.isArray(data)) {
      if (data.length > 0) cache = data;
      return { status: "ok", groups: data };
    }
    return { status: "error" };
  } catch (err) {
    if ((err as { name?: string }).name === "AbortError") return { status: "aborted" };
    console.error("[listMagasins] fetch error", err);
    return { status: "error" };
  }
}
